import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur } from "@/lib/verticals";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Check, CalendarBlank, Clock, UsersThree, BellSimple } from "@phosphor-icons/react";

const nextDays = (n) => {
  const days = [];
  for (let i = 0; i < n; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  return days;
};
const fmtDate = (d) => d.toISOString().slice(0, 10);
const DOW = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];

const BookingDialog = ({ open, onOpenChange, provider, initialService }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [service, setService] = useState(initialService || null);
  const [selDate, setSelDate] = useState(fmtDate(new Date()));
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [staff, setStaff] = useState([]);
  const [selStaff, setSelStaff] = useState(null);
  const [closed, setClosed] = useState(false);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (open && provider?.provider_id) {
      api.get(`/providers/${provider.provider_id}/staff`).then((r) => setStaff(r.data)).catch(() => setStaff([]));
    }
  }, [open, provider]);

  useEffect(() => {
    if (open) {
      setStep(initialService ? 2 : 1);
      setService(initialService || null);
      setSlot(null);
      setSelStaff(null);
    }
  }, [open, initialService]);

  useEffect(() => {
    if (step === 2 && service) {
      api.get(`/providers/${provider.provider_id}/availability`, {
        params: { date: selDate, service_id: service.service_id, ...(selStaff ? { staff_id: selStaff } : {}) },
      }).then((r) => { setSlots(r.data.slots); setClosed(!!r.data.closed); }).catch(() => { setSlots([]); setClosed(false); });
    }
  }, [step, service, selDate, provider, selStaff]);

  const joinWaitlist = async () => {
    if (!user) { toast.error("Accedi per la lista d'attesa"); navigate("/login"); return; }
    setJoining(true);
    try {
      await api.post("/waitlist", { provider_id: provider.provider_id, service_id: service.service_id, date: selDate, staff_id: selStaff });
      toast.success("Sei in lista d'attesa! Ti avviseremo se si libera un posto.");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setJoining(false); }
  };

  const confirm = async () => {
    if (!user) { toast.error("Accedi per prenotare"); navigate("/login"); return; }
    if (user.role !== "cliente") { toast.error("Solo i clienti possono prenotare"); return; }
    setLoading(true);
    try {
      const res = await api.post("/bookings", {
        provider_id: provider.provider_id, service_id: service.service_id,
        date: selDate, start_time: slot, staff_id: selStaff,
      });
      const co = await api.post("/payments/checkout", {
        booking_id: res.data.booking_id, origin_url: window.location.origin,
      });
      window.location.href = co.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore prenotazione");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0" data-testid="booking-dialog">
        <DialogHeader className="p-5 pb-3 border-b">
          <DialogTitle className="font-heading">Prenota — {provider?.business_name}</DialogTitle>
          <div className="flex gap-1.5 pt-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? "bg-primary" : "bg-muted"}`} />
            ))}
          </div>
        </DialogHeader>

        <div className="p-5 max-h-[65vh] overflow-y-auto">
          {step === 1 && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground mb-3">Scegli un servizio</p>
              {provider.services?.map((s) => (
                <button key={s.service_id} data-testid={`svc-option-${s.service_id}`}
                  onClick={() => { setService(s); setStep(2); }}
                  className="w-full flex items-center justify-between p-3 rounded-xl border border-border hover:border-primary hover:bg-accent transition-colors text-left">
                  <div>
                    <div className="font-medium text-sm">{s.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={12} />{s.duration_min} min</div>
                  </div>
                  <span className="font-semibold text-primary">{eur(s.price)}</span>
                </button>
              ))}
            </div>
          )}

          {step === 2 && (
            <div>
              {staff.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1"><UsersThree size={13} /> Scegli operatore</p>
                  <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                    <button data-testid="staff-any" onClick={() => setSelStaff(null)}
                      className={`shrink-0 px-3 py-1.5 rounded-full text-sm border transition-colors ${!selStaff ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>Qualsiasi</button>
                    {staff.map((m) => (
                      <button key={m.staff_id} data-testid={`staff-pick-${m.staff_id}`} onClick={() => { setSelStaff(m.staff_id); setSlot(null); }}
                        className={`shrink-0 px-3 py-1.5 rounded-full text-sm border transition-colors ${selStaff === m.staff_id ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{m.name}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3">
                {nextDays(14).map((d) => {
                  const ds = fmtDate(d);
                  return (
                    <button key={ds} data-testid={`date-${ds}`}
                      onClick={() => { setSelDate(ds); setSlot(null); }}
                      className={`shrink-0 w-14 py-2 rounded-xl border text-center transition-colors ${selDate === ds ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
                      <div className="text-[10px] uppercase opacity-70">{DOW[d.getDay()]}</div>
                      <div className="font-semibold">{d.getDate()}</div>
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {slots.length === 0 && <p className="col-span-4 text-sm text-muted-foreground py-6 text-center">{closed ? "Chiuso in questa data (ferie o giorno di chiusura)" : "Nessuno slot disponibile"}</p>}
                {slots.map((sl) => (
                  <motion.button key={sl.time} whileTap={{ scale: 0.94 }} disabled={!sl.available}
                    data-testid={`slot-${sl.time}`}
                    onClick={() => setSlot(sl.time)}
                    className={`py-2 rounded-lg text-sm border transition-colors ${slot === sl.time ? "bg-primary text-primary-foreground border-primary" : sl.available ? "border-border hover:border-primary" : "opacity-30 cursor-not-allowed line-through"}`}>
                    {sl.time}
                  </motion.button>
                ))}
              </div>
              {slots.every((sl) => !sl.available) && (
                <Button variant="outline" className="w-full mt-4 rounded-full gap-1" onClick={joinWaitlist} disabled={joining} data-testid="join-waitlist">
                  <BellSimple weight="fill" /> {joining ? "Iscrizione…" : "Mettiti in lista d'attesa per questa data"}
                </Button>
              )}
              <Button className="w-full mt-3 rounded-full" disabled={!slot} data-testid="booking-to-confirm"
                onClick={() => setStep(3)}>Continua</Button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="rounded-xl border border-border p-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Servizio</span><span className="font-medium">{service.name}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground flex items-center gap-1"><CalendarBlank size={14}/>Data</span><span className="font-medium">{selDate} — {slot}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Durata</span><span className="font-medium">{service.duration_min} min</span></div>
                <div className="border-t pt-2 flex justify-between"><span className="font-semibold">Totale</span><span className="font-bold text-primary text-lg">{eur(service.price)}</span></div>
              </div>
              <p className="text-xs text-muted-foreground text-center">Pagamento sicuro con Stripe. Carta test: 4242 4242 4242 4242</p>
              <Button className="w-full rounded-full" onClick={confirm} disabled={loading} data-testid="booking-confirm-pay">
                {loading ? "Reindirizzamento…" : <><Check className="mr-1" weight="bold" /> Conferma e Paga {eur(service.price)}</>}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingDialog;
